import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchPlanManagerClientConfigs } from "../../../utils/apiUtils.js";
import { PLAN_MANAGER_QUERY_KEYS } from "../common/constants";
import {
    cycleCustomSiteListSort,
    filterCustomSitesBySearchCriteria,
    mapClientConfigsToCustomSiteRows,
    sortCustomSites,
} from "../../../utils/planManager/preEnrollmentCustomUtils.js";


/**
 * Delay before search text is applied to the table, matching CustomSitesManagement.vue.
 * @constant {number}
 */
export const CUSTOM_SITE_SEARCH_DEBOUNCE_MS = 400;


/**
 * Buefy `default-sort="clientName"` — first paint is Client Name, ascending.
 * @constant {string}
 */
export const DEFAULT_CUSTOM_SITE_SORTED_FIELD = "clientName";

const PRE_ENROLLMENT_CUSTOM_LOAD_ERROR_MESSAGE =
    "Plan manager data is currently unavailable. Please try again later.";

/**
 * Ephemeral UI state and handlers for the pre-enrollment custom list screen.
 * @returns {object} Values and callbacks for the presentational view.
 */
export function usePreEnrollmentCustom() {
    const [searchCriteria, setSearchCriteria] = useState("");
    const [appliedSearchCriteria, setAppliedSearchCriteria] = useState("");
    const [sortedField, setSortedField] = useState(DEFAULT_CUSTOM_SITE_SORTED_FIELD);
    const [isSortDescending, setIsSortDescending] = useState(false);

    useEffect(() => {
        const debounceTimeoutId = setTimeout(() => {
            setAppliedSearchCriteria(searchCriteria);
        }, CUSTOM_SITE_SEARCH_DEBOUNCE_MS);

        return () => clearTimeout(debounceTimeoutId);
    }, [searchCriteria]);

    const clientConfigsQuery = useQuery({
        queryKey: PLAN_MANAGER_QUERY_KEYS.clientConfigs({ isCustom: true }),
        queryFn: () => fetchPlanManagerClientConfigs({ isCustom: true }),
    });

    const customSites = useMemo(
        () => mapClientConfigsToCustomSiteRows(clientConfigsQuery.data ?? []),
        [clientConfigsQuery.data],
    );

    const visibleCustomSites = useMemo(() => {
        const matchingRows = filterCustomSitesBySearchCriteria(
            customSites,
            appliedSearchCriteria,
        );

        return sortCustomSites(matchingRows, sortedField, isSortDescending);
    }, [customSites, appliedSearchCriteria, sortedField, isSortDescending]);

    const handleSort = (field) => {
        const nextSort = cycleCustomSiteListSort(
            sortedField,
            isSortDescending,
            field,
        );

        setSortedField(nextSort.sortedField);
        setIsSortDescending(nextSort.isSortDescending);
    };

    const isLoading = clientConfigsQuery.isLoading;
    const isError = clientConfigsQuery.isError;
    const loadErrorMessage =
        clientConfigsQuery.error?.message ??
        PRE_ENROLLMENT_CUSTOM_LOAD_ERROR_MESSAGE;

    return {
        searchCriteria,
        setSearchCriteria,
        visibleCustomSites,
        appliedSearchCriteria,
        sortedField,
        isSortDescending,
        handleSort,
        isLoading,
        isError,
        loadErrorMessage,
    };
}
